import { delay } from '@aytm/lib/common/promise'

import { StatsChatAssistantState, StatsChatContext } from '../types'
import ChatError from '../../../common/ChatError'

declare global {
  interface Window {
    StatPage?: {
      model?: {
        getApiKey(): { code: string; expire: number } | null
        isApiKeyInvalid(): boolean
      }
      bridge?: {
        // Kicks off the request and returns nothing — the fresh key arrives on the model, not here
        fetchApiKey(): void
      }
    }
  }
}

export default async function awaitContext({
  assistantState
}: {
  assistantState: StatsChatAssistantState
}): Promise<StatsChatContext> {
  if ('function' === typeof assistantState.contextGetter) {
    const context = assistantState.contextGetter()
    if (context?.token && context?.expire) {
      return context
    }
  }

  // wait for windows.StatPage.model to be available before getting API token
  let attempts = 0
  const maxAttempts = 20

  while (attempts < maxAttempts && !window.StatPage?.model?.getApiKey) {
    await delay(500)
    attempts++
  }

  if (!window.StatPage?.model?.getApiKey) {
    throw new ChatError('StatPage.model.getApiKey is not available')
  }

  let apiKeyData = window.StatPage.model.getApiKey()

  if (!apiKeyData || window.StatPage.model.isApiKeyInvalid()) {
    if (!window.StatPage?.bridge?.fetchApiKey) {
      throw new ChatError('API key is invalid and StatPage.bridge.fetchApiKey is not available')
    }

    try {
      window.StatPage.bridge.fetchApiKey()
    } catch (error) {
      throw new ChatError(
        'Failed to fetch valid API key: ' + (error instanceof Error ? error.message : 'Unknown error')
      )
    }

    // fetchApiKey only sends the request — awaiting it hands back the stale key it was called to
    // replace, so wait for the model to actually carry a fresh one.
    attempts = 0
    while (attempts < maxAttempts && window.StatPage.model.isApiKeyInvalid()) {
      await delay(500)
      attempts++
    }

    apiKeyData = window.StatPage.model.getApiKey()
  }

  if (!apiKeyData?.code || !apiKeyData?.expire) {
    throw new ChatError('Unable to obtain valid API key')
  }

  return {
    token: apiKeyData.code,
    expire: apiKeyData.expire
  }
}
